import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  createRegionalPricing,
  updateRegionalPricing,
  clearError
} from "../../redux/Slices/RegionalPricingSlice";
import {
  fetchCurrencies
} from "../../redux/Slices/CurrencySlice";
import {
  fetchCountries
} from "../../redux/Slices/LocationSlice";
import {
  CreateRegionalPricingDto,
  UpdateRegionalPricingDto,
  RegionalPricingTypeEnum
} from "../../modules/shared/model";
import type { RegionalPricing } from "../../modules/shared/model";

interface RegionalPricingModalProps {
  region: RegionalPricing | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const RegionalPricingModal: React.FC<RegionalPricingModalProps> = ({
  region,
  isOpen,
  onClose,
  onSuccess
}) => {
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector(state => state.regionalPricings);
  const { items: currencies } = useAppSelector(state => state.currencies);
  const { countries } = useAppSelector(state => state.locations);

  const [formData, setFormData] = useState<CreateRegionalPricingDto>({
    name: "",
    type: { id: RegionalPricingTypeEnum.City },
    currency: null,
    country: null,
    enableSeasonalPricing: false,
    enablePeakHours: false,
    enableHolidayRates: false,
    enableWeekendRates: false,
    ServiceChargesEmergency: 0,
    ServiceChargesTransaction: 0,
    ServiceChargesPlatform: 0,
    subscriptionPlanEnterprise: 0,
    subscriptionPlanPlatform: 0,
    subscriptionPlanBasic: 0
  });

  // Load currencies and countries on component mount
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCurrencies({ page: 1, limit: 100 }));
      dispatch(fetchCountries({ page: 1, limit: 100 }));
    }
  }, [dispatch, isOpen]);

  // Ensure we have arrays to work with
  const safeCurrencies = currencies || [];
  const safeCountries = countries || [];

  // Populate form data when editing
  useEffect(() => {
    if (region && isOpen) {
      setFormData({
        name: region.name || "",
        type: region.type ? { id: Number(region.type.id) } : { id: RegionalPricingTypeEnum.City },
        currency: region.currency ? { id: region.currency.id } : null,
        country: region.country ? { id: region.country.id } : null,
        enableSeasonalPricing: region.enableSeasonalPricing || false,
        enablePeakHours: region.enablePeakHours || false,
        enableHolidayRates: region.enableHolidayRates || false,
        enableWeekendRates: region.enableWeekendRates || false,
        ServiceChargesEmergency: region.ServiceChargesEmergency || 0,
        ServiceChargesTransaction: region.ServiceChargesTransaction || 0,
        ServiceChargesPlatform: region.ServiceChargesPlatform || 0,
        subscriptionPlanEnterprise: region.subscriptionPlanEnterprise || 0,
        subscriptionPlanPlatform: region.subscriptionPlanPlatform || 0,
        subscriptionPlanBasic: region.subscriptionPlanBasic || 0
      });
    } else if (isOpen) {
      // Reset form for new entry
      setFormData({
        name: "",
        type: { id: RegionalPricingTypeEnum.City },
        currency: null,
        country: null,
        enableSeasonalPricing: false,
        enablePeakHours: false,
        enableHolidayRates: false,
        enableWeekendRates: false,
        ServiceChargesEmergency: 0,
        ServiceChargesTransaction: 0,
        ServiceChargesPlatform: 0,
        subscriptionPlanEnterprise: 0,
        subscriptionPlanPlatform: 0,
        subscriptionPlanBasic: 0
      });
    }
  }, [region, isOpen]);

  // Clear error when component unmounts or modal closes
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCreate = async () => {
    try {
      await dispatch(createRegionalPricing(formData));
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Failed to create regional pricing:", err);
    }
  };

  const handleUpdate = async () => {
    if (!region) return;
    
    try {
      await dispatch(updateRegionalPricing({ 
        id: region.id, 
        data: formData as UpdateRegionalPricingDto 
      }));
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Failed to update regional pricing:", err);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b sticky top-0 bg-white" style={{ textAlign: "left" }}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {region ? "Edit Regional Pricing" : "Add Regional Pricing"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Region Name</label>
              <input
                type="text"
                value={formData.name || ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full border rounded-lg px-3 py-2"
                placeholder="Enter region name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select 
                  value={formData.type?.id || RegionalPricingTypeEnum.City}
                  onChange={(e) => setFormData({ ...formData, type: { id: Number(e.target.value) } })}
                  className="mt-1 block w-full border rounded-lg px-3 py-2"
                >
                  <option value={RegionalPricingTypeEnum.City}>City</option>
                  <option value={RegionalPricingTypeEnum.Metropolitan}>Metropolitan</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Currency</label>
                <select
                  value={formData.currency?.id || ""}
                  onChange={(e) => setFormData({ ...formData, currency: { id: e.target.value } })}
                  className="mt-1 block w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Select Currency</option>
                  {safeCurrencies.length > 0 ? (
                    safeCurrencies.map((currency) => (
                      <option key={currency.id} value={currency.id}>
                        {currency.name} ({currency.initial})
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Loading currencies...</option>
                  )}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Country</label>
                <select
                  value={formData.country?.id || ""}
                  onChange={(e) => setFormData({ ...formData, country: { id: e.target.value } })}
                  className="mt-1 block w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Select Country</option>
                  {safeCountries.length > 0 ? (
                    safeCountries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name} ({country.initial})
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Loading countries...</option>
                  )}
                </select>
            </div>
          </div>

          {/* Subscription Plan Adjustments */}
          <div className="space-y-4">
            <h3 className="font-medium">Subscription Plan Prices</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Basic</label>
                <input
                  type="number"
                  value={formData.subscriptionPlanBasic || 0}
                  onChange={(e) => setFormData({ ...formData, subscriptionPlanBasic: Number(e.target.value) })}
                  className="mt-1 block w-full border rounded-lg px-3 py-2"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Platform</label>
                <input
                  type="number"
                  value={formData.subscriptionPlanPlatform || 0}
                  onChange={(e) => setFormData({ ...formData, subscriptionPlanPlatform: Number(e.target.value) })}
                  className="mt-1 block w-full border rounded-lg px-3 py-2"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Enterprise</label>
                <input
                  type="number"
                  value={formData.subscriptionPlanEnterprise || 0}
                  onChange={(e) => setFormData({ ...formData, subscriptionPlanEnterprise: Number(e.target.value) })}
                  className="mt-1 block w-full border rounded-lg px-3 py-2"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Service Charges */}
          <div className="space-y-4">
            <h3 className="font-medium">Service Charges</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Platform Fee</label>
                <input
                  type="number"
                  value={formData.ServiceChargesPlatform || 0}
                  onChange={(e) => setFormData({ ...formData, ServiceChargesPlatform: Number(e.target.value) })}
                  className="mt-1 block w-full border rounded-lg px-3 py-2"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Transaction Fee</label>
                <input
                  type="number"
                  value={formData.ServiceChargesTransaction || 0}
                  onChange={(e) => setFormData({ ...formData, ServiceChargesTransaction: Number(e.target.value) })}
                  className="mt-1 block w-full border rounded-lg px-3 py-2"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Emergency Fee</label>
                <input
                  type="number"
                  value={formData.ServiceChargesEmergency || 0}
                  onChange={(e) => setFormData({ ...formData, ServiceChargesEmergency: Number(e.target.value) })}
                  className="mt-1 block w-full border rounded-lg px-3 py-2"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Price Modifiers */}
          <div className="space-y-4">
            <h3 className="font-medium">Price Modifiers</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.enableWeekendRates || false}
                  onChange={(e) => setFormData({ ...formData, enableWeekendRates: e.target.checked })}
                  className="rounded border-gray-300" 
                />
                <span>Weekend Rates</span>
              </label>
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.enableHolidayRates || false}
                  onChange={(e) => setFormData({ ...formData, enableHolidayRates: e.target.checked })}
                  className="rounded border-gray-300" 
                />
                <span>Holiday Rates</span>
              </label>
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.enablePeakHours || false}
                  onChange={(e) => setFormData({ ...formData, enablePeakHours: e.target.checked })}
                  className="rounded border-gray-300" 
                />
                <span>Peak Hours</span>
              </label>
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.enableSeasonalPricing || false}
                  onChange={(e) => setFormData({ ...formData, enableSeasonalPricing: e.target.checked })}
                  className="rounded border-gray-300" 
                />
                <span>Seasonal Pricing</span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={region ? handleUpdate : handleCreate}
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {region ? "Save Changes" : "Add Region"}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default RegionalPricingModal;
